import { useRouter } from "@tanstack/react-router";
import { Check, ChevronDown, ChevronRight, FileText, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import type { PlaceResult } from "@/components/ui/PlacesAutocompleteInput";
import { PlacesAutocompleteInput } from "@/components/ui/PlacesAutocompleteInput";
import { useCompanies } from "@/hooks/use-companies";
import { useContacts } from "@/hooks/use-contacts";
import { useProjectTeamRoles } from "@/hooks/use-project-team-roles";
import { useCreateProject } from "@/hooks/use-projects";
import { apiPost } from "@/lib/api";

const STEPS = [
  "Basic Info",
  "Template",
  "Location & Client",
  "Team",
  "Schedule & Budget",
  "Review & Submit",
];

const PROJECT_STATUSES = [
  { value: "pre-construction", label: "Pre-Construction" },
  { value: "active", label: "Active" },
  { value: "on-hold", label: "On Hold" },
];

interface TeamAssignment {
  contactId: string;
  roleId: string;
}

interface FormData {
  name: string;
  projectNumber: string;
  type: string;
  status: string;
  description: string;
  location: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  client: string;
  clientCompanyId: string;
  generalContractorCompanyId: string;
  startDate: string;
  endDate: string;
  contractValue: string;
  teamMembers: TeamAssignment[];
}

const initialFormData: FormData = {
  name: "",
  projectNumber: "",
  type: "general_contracting",
  status: "active",
  description: "",
  location: "",
  addressLine1: "",
  city: "",
  state: "",
  zip: "",
  client: "",
  clientCompanyId: "",
  generalContractorCompanyId: "",
  startDate: "",
  endDate: "",
  contractValue: "",
  teamMembers: [],
};

interface TaskTemplate {
  id: string;
  name: string;
  description: string | null;
  projectType: string | null;
  status: string;
}

type StepErrors = Record<string, string>;

function validateStep(
  step: number,
  data: FormData,
  ctx: { roleKeyById: Record<string, string> } = { roleKeyById: {} },
): StepErrors {
  const errors: StepErrors = {};
  if (step === 0) {
    if (!data.name.trim()) errors.name = "Project name is required";
    if (!data.status) errors.status = "Status is required";
  } else if (step === 2) {
    if (!data.location.trim()) errors.location = "Location is required";
    if (!data.client.trim()) errors.client = "Client name is required";
  } else if (step === 3) {
    const hasRoleKey = (key: string) =>
      data.teamMembers.some((m) => ctx.roleKeyById[m.roleId] === key);
    if (!hasRoleKey("project_manager"))
      errors.project_manager = "At least one Project Manager is required";
    if (!hasRoleKey("superintendent"))
      errors.superintendent = "At least one Superintendent is required";
  } else if (step === 4) {
    if (!data.startDate) errors.startDate = "Start date is required";
    if (!data.endDate) errors.endDate = "End date is required";
    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      errors.endDate = "End date must be after start date";
    }
    if (!data.contractValue || Number(data.contractValue) <= 0) {
      errors.contractValue = "Contract value must be greater than 0";
    }
  }
  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-red-500 text-xs mt-1">{message}</p>;
}

interface SearchableOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface SearchableSelectProps {
  value: string;
  onSelect: (value: string) => void;
  options: SearchableOption[];
  placeholder: string;
  emptyText: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
  hasError?: boolean;
  /** When true, clears search/selection after picking — useful for "add to list" pickers. */
  clearOnSelect?: boolean;
}

function SearchableSelect({
  value,
  onSelect,
  options,
  placeholder,
  emptyText,
  disabled = false,
  disabledPlaceholder,
  hasError = false,
  clearOnSelect = false,
}: SearchableSelectProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sublabel ?? "").toLowerCase().includes(q),
    );
  }, [options, search]);

  const inputClasses = `w-full px-3 py-2 pr-8 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${
    hasError ? "border-red-400" : "border-gray-300"
  }`;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value && !clearOnSelect ? (selected?.label ?? "") : search}
          onChange={(e) => {
            if (value && !clearOnSelect) onSelect("");
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          placeholder={disabled && disabledPlaceholder ? disabledPlaceholder : placeholder}
          className={inputClasses}
        />
        {value && !clearOnSelect ? (
          <button
            type="button"
            onClick={() => {
              onSelect("");
              setSearch("");
              setOpen(true);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
      </div>
      {open && !disabled && (!value || clearOnSelect) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyText}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                onClick={() => {
                  onSelect(o.value);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <div className="font-medium text-gray-800">{o.label}</div>
                {o.sublabel && <div className="text-xs text-gray-500">{o.sublabel}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  type: string;
  companyId: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewProjectDialog({ open, onOpenChange }: Props) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const createProject = useCreateProject();
  const router = useRouter();
  const { data: contactsData } = useContacts();
  const { data: clientCompaniesData } = useCompanies("owner");
  const { data: gcCompaniesData } = useCompanies("general_contractor");
  const { data: rolesData } = useProjectTeamRoles();
  const contacts: Contact[] = (contactsData ?? []) as Contact[];
  const clientCompanies = clientCompaniesData ?? [];
  const gcCompanies = gcCompaniesData ?? [];
  const roles = useMemo(() => rolesData ?? [], [rolesData]);
  const roleKeyById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of roles) out[r.id] = r.key;
    return out;
  }, [roles]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  const filteredClientCompanies = useMemo(() => {
    if (!clientSearch.trim()) return clientCompanies;
    const q = clientSearch.toLowerCase();
    return clientCompanies.filter((c) => c.companyName.toLowerCase().includes(q));
  }, [clientCompanies, clientSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const gcContacts = useMemo(
    () =>
      formData.generalContractorCompanyId
        ? contacts.filter((c) => c.companyId === formData.generalContractorCompanyId)
        : contacts,
    [contacts, formData.generalContractorCompanyId],
  );

  // When only one general_contractor company exists, pre-select it.
  useEffect(() => {
    if (!open) return;
    if (formData.generalContractorCompanyId) return;
    if (gcCompanies.length !== 1) return;
    const only = gcCompanies[0];
    if (!only) return;
    setFormData((prev) => ({ ...prev, generalContractorCompanyId: only.id }));
  }, [open, gcCompanies, formData.generalContractorCompanyId]);

  useEffect(() => {
    if (currentStep !== 1 || !formData.type || !open) return;
    const controller = new AbortController();
    setTemplatesLoading(true);
    fetch("/api/task-templates", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: unknown) => {
        const list: TaskTemplate[] = Array.isArray(data) ? data : [];
        setTemplates(list);
        const first = list[0];
        if (first) setSelectedTemplateId(first.id);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setTemplates([]);
      })
      .finally(() => {
        // Don't flip the spinner off if we were aborted — a newer effect run
        // is already in flight and owns the loading state.
        if (!controller.signal.aborted) setTemplatesLoading(false);
      });
    return () => controller.abort();
  }, [currentStep, formData.type, open]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const update = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [errors],
  );

  function reset() {
    setCurrentStep(0);
    setFormData(initialFormData);
    setErrors({});
    setSubmitError("");
    setSelectedTemplateId(null);
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  function goNext() {
    const stepErrors = validateStep(currentStep, formData, { roleKeyById });
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep((s) => s + 1);
  }

  function goBack() {
    setErrors({});
    setCurrentStep((s) => s - 1);
  }

  function goToStep(step: number) {
    if (step < currentStep) {
      setErrors({});
      setCurrentStep(step);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    try {
      const result = await createProject.mutateAsync({
        name: formData.name.trim(),
        type: formData.type,
        status: formData.status,
        projectNumber: formData.projectNumber.trim() || undefined,
        description: formData.description.trim() || undefined,
        location: formData.location.trim(),
        addressLine1: formData.addressLine1 || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zip: formData.zip || undefined,
        client: formData.client.trim(),
        clientCompanyId: formData.clientCompanyId || undefined,
        generalContractorCompanyId: formData.generalContractorCompanyId || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate,
        contractValue: formData.contractValue,
        contractDate: formData.startDate || undefined,
        templateId: selectedTemplateId || undefined,
      } as Record<string, unknown>);

      const project = (result as { data?: { id?: string } }).data;

      if (project?.id && formData.teamMembers.length > 0) {
        await Promise.all(
          formData.teamMembers.map((m) =>
            apiPost(`/projects/${project.id}/team`, {
              contactId: m.contactId,
              roleId: m.roleId,
            }),
          ),
        );
      }

      if (selectedTemplateId && project?.id) {
        try {
          await fetch(`/api/projects/${project.id}/tasks/from-template`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ templateId: selectedTemplateId }),
          });
        } catch {
          // Template task generation failed silently
        }
      }

      handleClose();
      if (project?.id) {
        router.navigate({ to: `/projects/${project.id}` });
      }
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : "Failed to create project");
    }
  }

  const inputClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 ${
      errors[field] ? "border-red-400" : "border-gray-300"
    }`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 px-4">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[50vw] max-h-[90vh] overflow-y-auto">
        {/* Header with step indicators */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-700">New Project</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-1">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-1 flex-1">
                {i > 0 && (
                  <div
                    className={`h-0.5 flex-1 ${i <= currentStep ? "bg-slate-600" : "bg-gray-200"}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => goToStep(i)}
                  className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-medium px-2 py-1 rounded-full transition-colors ${
                    i === currentStep
                      ? "bg-slate-700 text-white"
                      : i < currentStep
                        ? "bg-slate-100 text-slate-600 cursor-pointer hover:bg-slate-600/20"
                        : "bg-gray-100 text-gray-400"
                  }`}
                  disabled={i > currentStep}
                >
                  {i < currentStep ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px]">
                      {i + 1}
                    </span>
                  )}
                  <span className="hidden sm:inline">{label}</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5">
          {/* Step 0: Basic Info */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => update("name", e.target.value)}
                    className={inputClass("name")}
                    placeholder="e.g. Downtown Office Tower"
                  />
                  <FieldError message={errors.name} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Number
                  </label>
                  <input
                    type="text"
                    value={formData.projectNumber}
                    onChange={(e) => update("projectNumber", e.target.value)}
                    className={inputClass("projectNumber")}
                    placeholder="e.g. PB-2025-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => update("status", e.target.value)}
                    className={inputClass("status")}
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <FieldError message={errors.status} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => update("description", e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 resize-none"
                  placeholder="Brief project description..."
                />
              </div>
            </div>
          )}

          {/* Step 1: Template */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-slate-700 mb-1">
                  Select a Task Template
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Optionally assign a task template to auto-generate project tasks. You can skip
                  this step.
                </p>
              </div>
              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setSelectedTemplateId(null)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                      selectedTemplateId === null
                        ? "border-slate-700 bg-slate-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedTemplateId === null ? "border-slate-700" : "border-gray-300"
                        }`}
                      >
                        {selectedTemplateId === null && (
                          <div className="w-2 h-2 rounded-full bg-slate-700" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">No template / Skip</p>
                        <p className="text-xs text-gray-500">
                          Start with no tasks — add them manually later
                        </p>
                      </div>
                    </div>
                  </button>
                  {templates.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                      No active templates found for this project type
                    </div>
                  ) : (
                    templates.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(tpl.id)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                          selectedTemplateId === tpl.id
                            ? "border-slate-600 bg-slate-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              selectedTemplateId === tpl.id ? "border-slate-600" : "border-gray-300"
                            }`}
                          >
                            {selectedTemplateId === tpl.id && (
                              <div className="w-2 h-2 rounded-full bg-slate-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <FileText className="w-4 h-4 text-slate-600 flex-shrink-0" />
                              <p className="text-sm font-medium text-slate-700">{tpl.name}</p>
                            </div>
                            {tpl.description && (
                              <p className="text-xs text-gray-500 mb-1">{tpl.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Location & Client */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location / Address <span className="text-red-400">*</span>
                </label>
                <PlacesAutocompleteInput
                  value={formData.location}
                  onChange={(v) => update("location", v)}
                  onSelect={(result: PlaceResult) => {
                    setFormData((prev) => ({
                      ...prev,
                      location: result.location,
                      addressLine1: result.addressLine1,
                      city: result.city,
                      state: result.state,
                      zip: result.zip,
                    }));
                  }}
                  inputClassName={inputClass("location")}
                />
                <FieldError message={errors.location} />
              </div>
              <div ref={clientDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.clientCompanyId ? formData.client : clientSearch}
                    onChange={(e) => {
                      if (formData.clientCompanyId) {
                        update("client", "");
                        update("clientCompanyId", "");
                      }
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    className={inputClass("client")}
                    placeholder="Search client companies..."
                  />
                  {formData.clientCompanyId ? (
                    <button
                      type="button"
                      onClick={() => {
                        update("client", "");
                        update("clientCompanyId", "");
                        setClientSearch("");
                        setShowClientDropdown(true);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : (
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  )}
                </div>
                {showClientDropdown && !formData.clientCompanyId && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredClientCompanies.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No client companies found
                      </div>
                    ) : (
                      filteredClientCompanies.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            update("client", c.companyName);
                            update("clientCompanyId", c.id);
                            setClientSearch("");
                            setShowClientDropdown(false);
                          }}
                        >
                          {c.companyName}
                        </button>
                      ))
                    )}
                  </div>
                )}
                <FieldError message={errors.client} />
              </div>
            </div>
          )}

          {/* Step 3: Team */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  General Contractor
                </label>
                <SearchableSelect
                  value={formData.generalContractorCompanyId}
                  onSelect={(id) => {
                    setFormData((prev) => ({
                      ...prev,
                      generalContractorCompanyId: id,
                      teamMembers: [],
                    }));
                  }}
                  options={gcCompanies.map((c) => ({
                    value: c.id,
                    label: c.companyName,
                  }))}
                  placeholder="Search general contractors..."
                  emptyText="No general contractors found"
                  hasError={Boolean(errors.generalContractorCompanyId)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Used as the default contractor for Owner Pay App cost codes (including Division
                  99). Team members are picked from this contractor's contacts.
                </p>
              </div>

              {roles.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  No team roles are configured. An admin can add roles in Settings → Project Team
                  Roles.
                </div>
              ) : (
                roles.map((role) => {
                  const isRequired =
                    role.key === "project_manager" || role.key === "superintendent";
                  const assigned = formData.teamMembers.filter((m) => m.roleId === role.id);
                  const assignedIds = new Set(assigned.map((m) => m.contactId));
                  const available = gcContacts.filter((c) => !assignedIds.has(c.id));
                  const errKey = role.key;
                  return (
                    <div key={role.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {role.label}
                        {isRequired && <span className="text-red-400"> *</span>}
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {assigned.length === 0 ? (
                          <span className="text-xs text-gray-400">No one assigned yet</span>
                        ) : (
                          assigned.map((m) => {
                            const c = gcContacts.find((g) => g.id === m.contactId);
                            const name = c ? `${c.firstName} ${c.lastName}` : "(unknown contact)";
                            return (
                              <span
                                key={`${m.roleId}:${m.contactId}`}
                                className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 rounded-full"
                              >
                                {name}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      teamMembers: prev.teamMembers.filter(
                                        (t) =>
                                          !(t.roleId === m.roleId && t.contactId === m.contactId),
                                      ),
                                    }));
                                  }}
                                  className="text-slate-400 hover:text-slate-700"
                                  aria-label={`Remove ${name}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                      <SearchableSelect
                        value=""
                        clearOnSelect
                        onSelect={(contactId) => {
                          if (!contactId) return;
                          setFormData((prev) => ({
                            ...prev,
                            teamMembers: [...prev.teamMembers, { contactId, roleId: role.id }],
                          }));
                          if (errors[errKey]) {
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next[errKey];
                              return next;
                            });
                          }
                        }}
                        options={available.map((p) => ({
                          value: p.id,
                          label: `${p.firstName} ${p.lastName}`,
                          sublabel: p.title ?? undefined,
                        }))}
                        placeholder={`Search ${role.label.toLowerCase()}s...`}
                        disabled={!formData.generalContractorCompanyId || available.length === 0}
                        disabledPlaceholder={
                          !formData.generalContractorCompanyId
                            ? "Select a GC first..."
                            : gcContacts.length === 0
                              ? "No contacts found for this GC"
                              : "All GC contacts already assigned to this role"
                        }
                        emptyText="No matching contacts"
                        hasError={Boolean(errors[errKey])}
                      />
                      <FieldError message={errors[errKey]} />
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Step 4: Schedule & Budget */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date <span className="text-red-400">*</span>
                  </label>
                  <DatePicker
                    value={formData.startDate || null}
                    onChange={(next) => update("startDate", next ?? "")}
                    ariaLabel="Start date"
                    className={`w-full ${errors.startDate ? "border-red-400" : ""}`}
                  />
                  <FieldError message={errors.startDate} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date <span className="text-red-400">*</span>
                  </label>
                  <DatePicker
                    value={formData.endDate || null}
                    onChange={(next) => update("endDate", next ?? "")}
                    ariaLabel="End date"
                    className={`w-full ${errors.endDate ? "border-red-400" : ""}`}
                  />
                  <FieldError message={errors.endDate} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Value ($) <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    value={formData.contractValue}
                    onChange={(e) => update("contractValue", e.target.value)}
                    className={`${inputClass("contractValue")} pl-7`}
                    placeholder="0"
                    min="0"
                    step="1"
                  />
                </div>
                <FieldError message={errors.contractValue} />
              </div>
            </div>
          )}

          {/* Step 5: Review & Submit */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm">Basic Info</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{" "}
                    <span className="font-medium">{formData.name}</span>
                  </div>
                  {formData.projectNumber && (
                    <div>
                      <span className="text-gray-500">Project #:</span>{" "}
                      <span className="font-medium">{formData.projectNumber}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Status:</span>{" "}
                    <span className="font-medium capitalize">
                      {formData.status.replace("-", " ")}
                    </span>
                  </div>
                </div>
                {formData.description && (
                  <p className="text-sm text-gray-600">{formData.description}</p>
                )}
                <button
                  type="button"
                  onClick={() => goToStep(0)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm">Template</h3>
                <div className="text-sm">
                  {selectedTemplate ? (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-600" />
                      <span className="font-medium text-slate-700">{selectedTemplate.name}</span>
                    </div>
                  ) : (
                    <span className="text-gray-500">No template selected</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm">Location & Client</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Location:</span>{" "}
                    <span className="font-medium">{formData.location}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Client:</span>{" "}
                    <span className="font-medium">{formData.client}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm">Team</h3>
                <div className="text-sm">
                  <div className="mb-2">
                    <span className="text-gray-500">General Contractor:</span>{" "}
                    <span className="font-medium">
                      {gcCompanies.find((c) => c.id === formData.generalContractorCompanyId)
                        ?.companyName ?? "Not set"}
                    </span>
                  </div>
                  {roles.map((role) => {
                    const members = formData.teamMembers.filter((m) => m.roleId === role.id);
                    if (members.length === 0) return null;
                    return (
                      <div key={role.id} className="mb-1">
                        <span className="text-gray-500">{role.label}:</span>{" "}
                        <span className="font-medium">
                          {members
                            .map((m) => {
                              const c = gcContacts.find((g) => g.id === m.contactId);
                              return c ? `${c.firstName} ${c.lastName}` : "(unknown)";
                            })
                            .join(", ")}
                        </span>
                      </div>
                    );
                  })}
                  {formData.teamMembers.length === 0 && (
                    <p className="text-gray-400">No team members assigned.</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(3)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm">Schedule & Budget</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Start:</span>{" "}
                    <span className="font-medium">
                      {formData.startDate
                        ? new Date(formData.startDate).toLocaleDateString()
                        : "--"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">End:</span>{" "}
                    <span className="font-medium">
                      {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : "--"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Contract Value:</span>{" "}
                    <span className="font-medium">
                      ${Number(formData.contractValue || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(4)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Edit
                </button>
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-between">
          <button
            type="button"
            onClick={currentStep === 0 ? handleClose : goBack}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {currentStep === 0 ? "Cancel" : "Back"}
          </button>
          {currentStep < STEPS.length - 1 ? (
            <div className="flex gap-2">
              {currentStep === 1 && (
                <button
                  type="button"
                  onClick={goNext}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Skip
                </button>
              )}
              <button
                type="button"
                onClick={goNext}
                className="flex items-center gap-2 px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors"
              >
                {currentStep === 1 && selectedTemplateId ? "Use Template" : "Next"}{" "}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createProject.isPending}
              className="flex items-center gap-2 px-5 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              {createProject.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Create Project
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
